import { useTranslation } from "react-i18next";
import { InputWithAlert } from "@/components/ui/input-with-alert";
import { SelectWithAlert } from "@/components/ui/select-with-alert";
import { DatePicker } from "@/components/ui/date-picker";
import {
  TRANSACTION_TYPE,
  PAYMENT_STATUS,
  PAYMENT_METHOD,
  SUPPORTED_CURRENCIES,
  transformFromIdValueToValueLabel,
  TRANSACTION_TYPE_MAP,
  PAYMENT_STATUS_MAP,
  PAYMENT_METHOD_MAP
} from "@/constants";
import { getBankFromSwiftCode, MBANK } from "@/bank_constants";
import { formatDateString } from "@/styles";
import { format } from "date-fns";

export const PaymentSection = ({ form, inputChecked = false }) => {
  const { t } = useTranslation();
  const empty_error = t('empty_data_invalid');

  const {
    isViewMode,
    typeOfPayment,
    setTypeOfPayment,
    wayOfPayment,
    setWayOfPayment,
    paymentStatus,
    setPaymentStatus,
    paymentDueDate,
    setPaymentDueDate,
    paymentCurrency,
    setPaymentCurrency,
    paymentCurrencyRate,
    paymentCurrencyRateDate,
    setPaymentCurrencyRateDate,
    paymentIban,
    bankAccounts,
    handleBankAccountSelect,
    issueDate,
    invoice,
  } = form;

  // View mode
  if (isViewMode) {
    return (
      <div className='w-full'>
        <div className="w-full text-xl font-medium py-6">
          {t("invoices:payment_title")}
        </div>
        <div className='w-full '>
          <div className='w-full flex justify-between flex-wrap'>
            <div className='w-full md:w-[49%] m-auto md:m-0'>
              <InputWithAlert
                label={t("invoices:transaction")}
                value={invoice ? TRANSACTION_TYPE_MAP(t)[invoice.typeOfPayment] ?? '' : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("invoices:payment_status")}
                value={invoice ? PAYMENT_STATUS_MAP(t)[invoice.paymentStatus] ?? '' : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("invoices:currency")}
                value={invoice?.paymentCurrency ?? ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("invoices:payment_account")}
                value={invoice ? `${invoice.paymentIban}  ${invoice.paymentCurrency}` : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
            </div>
            <div className='w-full md:w-[49%] m-auto md:m-0'>
              <InputWithAlert
                label={t("invoices:way_of_payment")}
                value={invoice ? PAYMENT_METHOD_MAP(t)[invoice.wayOfPayment] ?? '' : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t("invoices:payment_due_date")}
                value={invoice ? formatDateString(invoice.paymentDueDate) : ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              <InputWithAlert
                label={t('invoices:currency_rate_PLN')}
                value={invoice?.paymentCurrencyRate ?? ''}
                readOnly={true}
                disabled
                inputClassName={'h-12 mt-2 mb-4 bg-white'}
              />
              {invoice?.paymentCurrencyRateDate && (
                <InputWithAlert
                  label={t("invoices:payment_currency_date")}
                  value={invoice?.paymentCurrencyRateDate ? format(new Date(invoice.paymentCurrencyRateDate), 'dd.MM.yyyy') : ''}
                  readOnly={true}
                  disabled
                  inputClassName={'h-12 mt-2 mb-4 bg-white'}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Create/Edit mode
  return (
    <div className='w-full'>
      <div className="w-full text-xl font-medium py-6">
        {t("invoices:payment_title")}
      </div>
      <div className='w-full flex justify-between flex-wrap'>
        <div className='w-full md:w-[49%] m-auto md:m-0'>
          <SelectWithAlert
            label={t("invoices:transaction") + '*'}
            options={TRANSACTION_TYPE(t).map(transformFromIdValueToValueLabel)}
            value={typeOfPayment ?? ''}
            onChangeValue={setTypeOfPayment}
            error={inputChecked && !typeOfPayment}
            errorMessage={empty_error}
            inputClassName={'h-12 mt-2 mb-4 bg-white'}
          />
          <SelectWithAlert
            label={t("invoices:payment_status") + '*'}
            options={PAYMENT_STATUS(t).map(transformFromIdValueToValueLabel)}
            onChangeValue={setPaymentStatus}
            error={inputChecked && !paymentStatus}
            errorMessage={empty_error}
            value={paymentStatus}
            inputClassName={'h-12 mt-2 mb-4 bg-white'}
          />
          <div className="w-full flex justify-between pt-2 pb-4">
            <div className="w-10/12">
              <DatePicker
                label={t("invoices:payment_currency_date")}
                date={paymentCurrencyRateDate}
                defaultMonth={paymentCurrencyRateDate}
                disabled={{ after: new Date() }}
                setDate={(date) => {
                  if (!date) return;
                  setPaymentCurrencyRateDate(date);
                }}
                inputClassName={'h-12 text-base'}
              />
            </div>
            <div className="w-2/12 ml-2">
              <SelectWithAlert
                label={t("invoices:currency")}
                options={SUPPORTED_CURRENCIES}
                onChangeValue={setPaymentCurrency}
                value={paymentCurrency}
                error={inputChecked && !paymentCurrency}
                errorMessage={empty_error}
                inputClassName={'h-12 bg-white'}
              />
            </div>
          </div>
          <SelectWithAlert
            label={t("invoices:payment_account") + "*"}
            placeholder={t("invoices:payment_account") + "*"}
            options={bankAccounts.map(o => ({
              value: o.account,
              label: `${o.account} ${o.currency} ${o.swift ? getBankFromSwiftCode(o.swift) : MBANK}`
            }))}
            onChangeValue={handleBankAccountSelect}
            value={paymentIban}
            error={inputChecked && !paymentIban}
            errorMessage={empty_error}
            inputClassName={'h-12 mt-2 mb-4 bg-white'}
          />
        </div>
        <div className='w-full md:w-[49%] m-auto md:m-0'>
          <SelectWithAlert
            label={t("invoices:way_of_payment") + '*'}
            options={PAYMENT_METHOD(t).map(transformFromIdValueToValueLabel)}
            value={wayOfPayment}
            onChangeValue={setWayOfPayment}
            error={inputChecked && !wayOfPayment}
            errorMessage={empty_error}
            inputClassName={'h-12 mt-2 mb-4 bg-white'}
          />
          <DatePicker
            label={t("invoices:payment_due_date")}
            date={paymentDueDate}
            defaultMonth={paymentDueDate}
            disabled={{ before: issueDate }}
            setDate={(date) => {
              if (!date) return;
              setPaymentDueDate(date);
            }}
            inputClassName={'h-12 mt-2 mb-4 text-base'}
          />
          <InputWithAlert
            label={t('invoices:currency_rate_PLN')}
            value={paymentCurrencyRate}
            onChange={() => {}}
            disabled
            inputClassName={'h-12 mt-2 mb-4 bg-white'}
          />
        </div>
      </div>
    </div>
  );
};
